import { ChatAppResponse, getCitationFilePath } from "../../api";
import { QueryPlanStep, getStepLabel, activityTypeLabels } from "../AnalysisPanel/agentPlanUtils";

// Escape a string for safe interpolation into an HTML attribute or text node.
// The citation markup below is emitted as a raw HTML string (later fed through
// DOMPurify + rehypeRaw), so any user/model-controlled value must be escaped.
const escapeHtml = (value: string): string =>
    value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");

export type CitationDetail = {
    reference: string;
    index: number;
    isWeb: boolean;
    activityId?: string;
    stepNumber?: number;
    stepLabel?: string;
    stepSource?: string;
};

type CitationFragment =
    | { type: "text"; value: string }
    | {
          type: "citation";
          detail: CitationDetail;
      };

type ActivityStepMeta = {
    stepNumber: number;
    stepLabel: string;
};

type HtmlParsedAnswer = {
    answerHtml: string;
    citations: CitationDetail[];
};

const isWebCitation = (reference: string) => reference.startsWith("http://") || reference.startsWith("https://");

const normalizeAnswerText = (answer: ChatAppResponse, isStreaming: boolean): string => {
    let parsedAnswer = answer.output_text.trim();

    if (isStreaming) {
        let lastIndex = parsedAnswer.length;
        for (let i = parsedAnswer.length - 1; i >= 0; i--) {
            if (parsedAnswer[i] === "]") {
                break;
            } else if (parsedAnswer[i] === "[") {
                lastIndex = i;
                break;
            }
        }
        parsedAnswer = parsedAnswer.substring(0, lastIndex);
    }

    return parsedAnswer;
};

const buildActivityStepMap = (answer: ChatAppResponse): Record<string, ActivityStepMeta> => {
    const mapping: Record<string, ActivityStepMeta> = {};
    const thoughts = answer.context?.thoughts;
    if (!Array.isArray(thoughts)) {
        return mapping;
    }

    const thoughtWithPlan = thoughts.find(thought => Array.isArray(thought.props?.query_plan));
    if (!thoughtWithPlan) {
        return mapping;
    }

    const planSteps = (thoughtWithPlan.props?.query_plan as QueryPlanStep[]) ?? [];
    planSteps.forEach((step, index) => {
        if (step && step.id !== undefined && step.id !== null) {
            mapping[String(step.id)] = {
                stepNumber: index + 1,
                stepLabel: getStepLabel(step)
            };
        }
    });

    return mapping;
};

// Split the answer into segments, isolating fenced code blocks (``` ... ```)
// from regular prose. Citations must never be injected into code blocks
// (e.g. mermaid diagrams), otherwise the citation <sup> markup and HTML entity
// escaping would corrupt the diagram definition and it would fail to render.
export const splitOutCodeBlocks = (text: string): { value: string; isCode: boolean }[] => {
    const segments: { value: string; isCode: boolean }[] = [];
    // Match fenced code blocks, including an optional (possibly unterminated)
    // trailing fence at the end of a still-streaming answer.
    const fenceRegex = /```[\s\S]*?(?:```|$)/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = fenceRegex.exec(text)) !== null) {
        if (match.index > lastIndex) {
            segments.push({ value: text.slice(lastIndex, match.index), isCode: false });
        }
        segments.push({ value: match[0], isCode: true });
        lastIndex = fenceRegex.lastIndex;
    }
    if (lastIndex < text.length) {
        segments.push({ value: text.slice(lastIndex), isCode: false });
    }
    return segments;
};

const collectCitations = (answer: ChatAppResponse, isStreaming: boolean): { fragments: CitationFragment[]; citations: CitationDetail[] } => {
    const possibleCitations = answer.context.data_points.citations || [];
    const citationActivityDetails = answer.context.data_points.citation_activity_details ?? {};
    const activitySteps = buildActivityStepMap(answer);
    const externalResults = answer.context.data_points.external_results_metadata || [];
    const parsedAnswer = normalizeAnswerText(answer, isStreaming);

    // Helper to resolve SharePoint filename to URL
    const resolveSharePointUrl = (citation: string): string => {
        // If it's already a URL, return as-is
        if (isWebCitation(citation)) {
            return citation;
        }
        // Check if this looks like a filename (has an extension)
        const hasFileExtension = /\.(pdf|docx?|xlsx?|pptx?|txt|html?|csv)$/i.test(citation);
        if (!hasFileExtension) {
            return citation;
        }

        // Look for matching SharePoint URL in external_results_metadata
        // Match by checking if the URL ends with the filename
        const matchingResult = externalResults.find(result => {
            if (!result.url) return false;
            const urlParts = result.url.split("/");
            const urlFilename = urlParts[urlParts.length - 1];
            return urlFilename === citation || decodeURIComponent(urlFilename) === citation;
        });

        return matchingResult?.url || citation;
    };

    const fragments: CitationFragment[] = [];
    const citationMap = new Map<string, CitationDetail>();
    const citationList: CitationDetail[] = [];

    // Process a citation token ("[foo]" without the brackets) found in prose.
    const handleCitationToken = (part: string) => {
        const isValidCitation = possibleCitations.some(citation => citation.endsWith(part));
        if (!isValidCitation) {
            fragments.push({ type: "text", value: `[${part}]` });
            return;
        }

        // Resolve SharePoint filename to URL if applicable
        const resolvedReference = resolveSharePointUrl(part);

        // Check if this resolved reference already exists
        const existing = citationMap.get(resolvedReference);
        if (existing) {
            fragments.push({ type: "citation", detail: existing });
            return;
        }

        const backendDetail = citationActivityDetails?.[part];
        const activityId = backendDetail?.id;
        const stepMeta = activityId ? activitySteps[String(activityId)] : undefined;

        // Get label from backend type using our mapping, or fallback to stepMeta
        const activityLabel = backendDetail?.type ? activityTypeLabels[backendDetail.type] || backendDetail.type : undefined;

        const detail: CitationDetail = {
            reference: resolvedReference,
            index: citationList.length + 1,
            isWeb: isWebCitation(resolvedReference),
            activityId: activityId !== undefined ? String(activityId) : undefined,
            stepNumber: backendDetail?.number ?? stepMeta?.stepNumber,
            stepLabel: activityLabel ?? stepMeta?.stepLabel,
            stepSource: backendDetail?.source
        };

        citationMap.set(resolvedReference, detail);
        citationList.push(detail);
        fragments.push({ type: "citation", detail });
    };

    // Walk the answer segment by segment. Fenced code blocks are emitted
    // verbatim (no citation replacement) so mermaid diagrams / code samples are
    // never corrupted; only prose segments are scanned for [citation] tokens.
    splitOutCodeBlocks(parsedAnswer).forEach(segment => {
        if (segment.isCode) {
            fragments.push({ type: "text", value: segment.value });
            return;
        }
        segment.value.split(/\[([^\]]+)\]/g).forEach((part, index) => {
            if (index % 2 === 0) {
                fragments.push({ type: "text", value: part });
            } else {
                handleCitationToken(part);
            }
        });
    });

    return { fragments, citations: citationList };
};

// Build the citation badge as a raw HTML string. We deliberately do NOT use
// react-dom/server's renderToStaticMarkup here: calling the server renderer from
// the client during a render/transition mutates ReactSharedInternals and, under
// React 19, corrupts the transition callback (H.S), causing a runtime crash
// ("Ie is not a function") on the next state update. The markup is fully static
// (the inline onClick never survives DOMPurify/rehypeRaw anyway), so a plain
// string template is both correct and safe.
const renderCitation = (detail: CitationDetail): string => {
    const stepBadgeLabel = detail.stepSource ?? detail.stepLabel;
    const stepBadgeTitle =
        detail.stepNumber !== undefined
            ? `Linked to Step ${detail.stepNumber}${detail.stepLabel ? `: ${detail.stepLabel}` : ""}${detail.stepSource ? ` (${detail.stepSource})` : ""}`
            : stepBadgeLabel
              ? `Linked to ${stepBadgeLabel}`
              : undefined;
    const supTitleAttr = stepBadgeTitle ? ` title="${escapeHtml(stepBadgeTitle)}"` : "";
    const supElement = `<sup${supTitleAttr}>${escapeHtml(String(detail.index))}</sup>`;

    if (detail.isWeb) {
        const href = escapeHtml(detail.reference);
        return (
            `<span class="citationBadgeContainer">` +
            `<a class="supContainer" title="${href}" href="${href}" target="_blank" rel="noopener noreferrer">` +
            `${supElement}</a></span>`
        );
    }

    const title = escapeHtml(detail.reference);
    return `<span class="citationBadgeContainer"><a class="supContainer" title="${title}">${supElement}</a></span>`;
};

export function parseAnswerToHtml(answer: ChatAppResponse, isStreaming: boolean, onCitationClicked: (citationFilePath: string) => void): HtmlParsedAnswer {
    const { fragments, citations } = collectCitations(answer, isStreaming);
    const answerHtml = fragments.map(fragment => (fragment.type === "text" ? fragment.value : renderCitation(fragment.detail))).join("");

    return {
        answerHtml,
        citations
    };
}

export function extractCitationDetails(answer: ChatAppResponse, isStreaming = false): CitationDetail[] {
    return collectCitations(answer, isStreaming).citations;
}
